import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/auth-context';
import { DashboardUser, fetchDashboard } from '../services/dashboard';
import { isRecord, normalizeArrayField } from '../utils/backendData';
import { applyVanerixBranding } from '../utils/branding';

type DashboardState = {
  loading: boolean;
  error: string | null;
  warnings: string[];
  users: DashboardUser[];
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

function isDashboardUser(value: unknown): value is DashboardUser {
  return isRecord(value);
}

export default function Dashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<DashboardState>({
    loading: true,
    error: null,
    warnings: [],
    users: [],
  });

  const load = async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetchDashboard(token);

      console.log('Dashboard response:', data);

      if (!data.success) {
        throw new Error(applyVanerixBranding(data.message ?? 'Dashboard request failed.'));
      }

      const warnings: string[] = [];
      const users = normalizeArrayField<unknown>(data.data, 'users', warnings).filter(
        (user, index): user is DashboardUser => {
          if (isDashboardUser(user)) return true;
          warnings.push(`users item ${index + 1} has an invalid format and could not be displayed.`);
          return false;
        }
      );

      setState((prev) => ({
        ...prev,
        loading: false,
        error: null,
        warnings: warnings.map(applyVanerixBranding),
        users,
      }));
    } catch (err) {
      const msg = applyVanerixBranding(
        err instanceof Error ? err.message : 'Failed to load dashboard.'
      );
      setState((prev) => ({ ...prev, loading: false, error: msg, warnings: [] }));
    }
  };

  useEffect(() => {
    void load();
    // token changes after login/register should refetch
  }, [token]);

  return (
      <Box
        component="main"
        sx={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        bgcolor: 'background.default',
        p: 2,
        pt: 4,
        }}
      >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 1100, width: '100%', borderRadius: '15px' }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="center">
            <Tooltip title="Registered users and their chat IDs" arrow>
              <Chip
                label="Vanerix"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  fontSize: 16,
                  bgcolor: 'grey.200',
                  color: 'grey.700',
                  borderRadius: '999px',
                  px: 1.5,
                  py: 2,
                  boxShadow: '0 8px 18px rgba(17, 24, 39, 0.15)',
                }}
              />
            </Tooltip>
          </Stack>

          {state.loading ? (
            <Stack alignItems="center" spacing={1}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading dashboard...
              </Typography>
            </Stack>
          ) : null}

          {state.error ? <Alert severity="error">{state.error}</Alert> : null}
          {state.warnings.length > 0 ? (
            <Alert severity="warning">
              {state.warnings.map((warning, index) => (
                <Typography key={`${warning}-${index}`} component="div" variant="body2">
                  {`- ${warning}`}
                </Typography>
              ))}
            </Alert>
          ) : null}

          {!state.loading && state.users.length > 0 ? (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                width: '100%',
                borderRadius: '15px',
                overflow: 'hidden',
                borderColor: 'grey.200',
                boxShadow: '0 8px 20px rgba(17, 24, 39, 0.08)',
              }}
            >
              <Table size="small" aria-label="registered users">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: 'grey.200',
                      background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700' }}
                    >
                      Email
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700'}}
                    >
                      Telegram
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700',}}
                    >
                      Vanerix Plan
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700',}}
                    >
                      Auto Trading
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700'}}
                    >
                      Subscription Expiration
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.users.map((user, index) => (
                    <TableRow
                      key={user.tg_chat_id ?? `${user.username ?? 'user'}-${index}`}
                      hover
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        navigate(`/satoshi-account/${user.tg_chat_id}`, {
                          state: { accountName: user.username },
                        })
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          navigate(`/satoshi-account/${user.tg_chat_id}`, {
                            state: { accountName: user.username },
                          });
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 150ms ease, transform 150ms ease',
                        '&:hover': { bgcolor: 'grey.50' },
                      }}
                    >
                      <TableCell align="center" sx={{ fontSize: 16, color: 'grey.900' }}>
                        {user.username ?? '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: 16, color: 'grey.900' }}>
                        {user.tg_chat_id ?? '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: 16, color: 'grey.900' }}>
                        {user.satoshiPlanName ? applyVanerixBranding(user.satoshiPlanName) : '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: 16, color: 'grey.900' }}>
                        {typeof user.auto_trading === 'boolean' ? (
                          <Chip
                            size="small"
                            label={user.auto_trading ? 'Enabled' : 'Disabled'}
                            color={user.auto_trading ? 'success' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: 16, color: 'grey.900', whiteSpace: 'nowrap' }}>
                        {formatDate(user.valid_till)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}

          {!state.loading && state.users.length === 0 && !state.error ? (
            <Alert severity="info">No users found.</Alert>
          ) : null}

        </Stack>
      </Paper>
    </Box>
  );
}
